// components/PlannerView.js - Vista del planificador con ordenamiento por distancia
import React, { useState } from 'react';
import MapComponent from './MapComponent';
import { useTecnicoColores } from '../hooks/useTecnicos';
import { authService } from '../services/authService';
import { ordenarOrdenesPorDistancia, formatearDistancia, OFICINA_COORDS } from '../utils/distanceUtils';

const PlannerView = () => {
  const tecnicoColors = useTecnicoColores();
  const [codigoPostal, setCodigoPostal] = useState("");
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCita, setSelectedCita] = useState(null);
  const [clientLocation, setClientLocation] = useState(null);
  const [ordenesGeodificadas, setOrdenesGeodificadas] = useState([]);
  const [loadingMapa, setLoadingMapa] = useState(false);

  // Geocodificación del código postal del cliente
  const geocodePostalCode = async (cp) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cp + ", España")}&limit=1`,
        {
          headers: { 
            'User-Agent': 'ClimatizacionZapata/1.0 (contacto@climatizacionzapata.es)' 
          }
        }
      );
      const data = await response.json();
      
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          cp: cp
        };
      }
      return null;
    } catch (error) {
      console.error("Error geocodificando código postal:", error);
      return null;
    }
  };

  const buscarDisponibilidad = async () => {
    setLoading(true);
    setError("");
    setResultado(null);
    setSelectedCita(null);
    setClientLocation(null);
    setOrdenesGeodificadas([]);

    try {
      console.log("🔍 Buscando disponibilidad para CP:", codigoPostal);
      
      const response = await fetch(`/.netlify/functions/getDisponibilidad?cp=${codigoPostal}`, { headers: { ...authService.authHeader() } });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
        return;
      }
      
      if (data.tecnico === "Sin asignar") {
        setError("Código postal no encontrado en nuestras zonas de servicio");
        return;
      }
      
      setResultado(data);
      console.log("✅ Disponibilidad obtenida:", data);
      
      // Geocodificar ubicación del cliente
      const clientGeo = await geocodePostalCode(codigoPostal);
      if (clientGeo) {
        setClientLocation(clientGeo);
        console.log("📍 Cliente geocodificado:", clientGeo);
      }
      
    } catch (err) {
      setError(`Error conectando con el servidor: ${err.message}`);
      console.error("❌ Error completo:", err);
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar órdenes de un día específico CON ORDENAMIENTO
  const cargarOrdenesDelDia = async (cita) => {
    setSelectedCita(cita);
    setLoadingMapa(true);
    setOrdenesGeodificadas([]);

    try {
      console.log(`🗺️ Cargando órdenes de ${cita.tecnico} para ${cita.fechaISO}`);
      
      const response = await fetch(
        `/.netlify/functions/geocodificarCita?fecha=${cita.fechaISO}&tecnico=${cita.tecnico}`
      , { headers: { ...authService.authHeader() } });
      
      if (!response.ok) {
        throw new Error(`Error geocodificando: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("✅ Respuesta de geocodificación:", data);
      
      // ✅ ORDENAR ÓRDENES POR DISTANCIA (de más lejana a más cercana)
      const ordenesOrdenadas = ordenarOrdenesPorDistancia(data.ordenes || []);
      setOrdenesGeodificadas(ordenesOrdenadas);
      
      // Mostrar debug info si no hay órdenes
      if (ordenesOrdenadas.length === 0) {
        console.warn("⚠️ No se encontraron órdenes. Debug info:", data.debug);
        setError(`No se encontraron órdenes para ${cita.tecnico} el ${cita.dia}. 
                 Workorders totales: ${data.debug?.totalWorkorders || 0}, 
                 Para el técnico: ${data.debug?.totalParaElTecnico || 0}, 
                 Para la fecha: ${data.debug?.totalParaLaFecha || 0}`);
      }
      
    } catch (err) {
      console.error("❌ Error cargando órdenes del día:", err);
      setError(`Error cargando mapa: ${err.message}`);
    } finally {
      setLoadingMapa(false);
    }
  };

  // Calcular centro del mapa
  const getMapCenter = () => {
    if (clientLocation && ordenesGeodificadas.length > 0) {
      const validWorkorders = ordenesGeodificadas.filter(wo => wo.lat && wo.lng);
      if (validWorkorders.length > 0) {
        const allPoints = [clientLocation, ...validWorkorders];
        const avgLat = allPoints.reduce((sum, point) => sum + point.lat, 0) / allPoints.length;
        const avgLng = allPoints.reduce((sum, point) => sum + point.lng, 0) / allPoints.length;
        return { lat: avgLat, lng: avgLng };
      }
    } 
    
    if (clientLocation) {
      return clientLocation;
    }
    
    if (ordenesGeodificadas.length > 0) {
      const validWorkorders = ordenesGeodificadas.filter(wo => wo.lat && wo.lng);
      if (validWorkorders.length > 0) {
        const avgLat = validWorkorders.reduce((sum, wo) => sum + wo.lat, 0) / validWorkorders.length;
        const avgLng = validWorkorders.reduce((sum, wo) => sum + wo.lng, 0) / validWorkorders.length;
        return { lat: avgLat, lng: avgLng };
      }
    }
    
    return { lat: 37.9922, lng: -1.1307 };
  };

  const mapCenter = getMapCenter();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Panel de búsqueda y resultados */}
      <div className="space-y-6">
        {/* Buscador */}
        <div className="bg-white p-6 rounded-xl shadow-lg border">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">🔍 Buscar disponibilidad</h2>
          
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Código postal (ej: 30002)"
              className="flex-1 border-2 border-gray-200 rounded-lg p-3 focus:border-red-500 focus:outline-none transition-colors"
              value={codigoPostal}
              onChange={(e) => setCodigoPostal(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarDisponibilidad()}
            />
            <button
              onClick={buscarDisponibilidad}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !codigoPostal.trim()}
            >
              {loading ? "🔄 Consultando..." : "Buscar"}
            </button>
          </div>
        </div>

        {/* Errores */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
            ❌ {error}
          </div>
        )}

        {/* Resultados */}
        {resultado && (
          <div className="bg-white p-6 rounded-xl shadow-lg border">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">📍 Información de la zona</h2>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 gap-2">
                <div><strong>Zona:</strong> {resultado.zona}</div>
                <div><strong>Técnico principal:</strong> {resultado.tecnicoPrincipal}</div>
                <div><strong>Código postal:</strong> {codigoPostal}</div>
                <div><strong>Órdenes esta semana:</strong> {resultado.allWorkorders?.length || 0}/80</div>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-3 text-gray-800">📆 Próximas citas disponibles</h3>

            {resultado.citas && resultado.citas.length > 0 ? (
              <div className="space-y-2">
                {resultado.citas.map((cita, idx) => (
                  <button
                    key={idx}
                    className={`relative w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedCita?.fechaISO === cita.fechaISO
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                    }`}
                    onClick={() => cargarOrdenesDelDia(cita)}
                    disabled={loadingMapa}
                  >
                    <div className="flex justify-between items-start">
                      {cita.instalacion && (
                        <div className="absolute -top-3 -left-3 bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                          ⚠ Instalación asignada
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-gray-800">{cita.dia}</div>
                        <div className="text-sm text-gray-600">
                          Técnico: {cita.tecnico}
                          {cita.apoyo && <span className="text-red-600"> (apoyo)</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-red-600">
                          {cita.huecos} huecos disponibles
                        </div>
                        {selectedCita?.fechaISO === cita.fechaISO && (
                          <div className="text-xs text-red-600 mt-1">
                            📍 Mostrando en mapa
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center p-6 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-yellow-800">
                  🚫 No hay citas disponibles en los próximos días
                </div>
                <div className="text-sm text-yellow-600 mt-2">
                  Contacte con el departamento de planificación
                </div>
              </div>
            )}

            {/* Información sobre cita seleccionada CON DISTANCIAS */}
            {selectedCita && ordenesGeodificadas.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800 mb-2">
                  📋 {selectedCita.dia}
                </h4>
                <div className="text-sm text-red-700 space-y-1 mb-3">
                  <div>Técnico: {selectedCita.tecnico}</div>
                  <div>Órdenes en el mapa: {ordenesGeodificadas.length}</div>
                  <div className="text-xs text-red-600">
                    📏 Ordenadas desde la más lejana hasta la más cercana a la oficina
                  </div>
                </div>
                
                <div className="mt-2 text-xs">
                  <strong>Ruta optimizada:</strong>
                  <ul className="mt-1 space-y-1 max-h-64 overflow-y-auto">
                    {ordenesGeodificadas.map((orden, idx) => (
                      <li key={idx} className="bg-white p-2 rounded border-l-4" style={{borderLeftColor: tecnicoColors[orden.tecnico]}}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">
                              📍 #{idx + 1} {orden.direccion || 'Dirección no disponible'}
                            </div>
                            <div className="text-gray-500 mt-1">
                              Distancia desde oficina: <strong>{formatearDistancia(orden.distanciaOficina)}</strong>
                              {orden.ordenOriginal && (
                                <span className="text-gray-400"> (orden original: #{orden.ordenOriginal})</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Debug info para geocodificación */}
            {selectedCita && ordenesGeodificadas.length === 0 && !loadingMapa && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  🔍 Debug: No se encontraron órdenes
                </h4>
                <div className="text-xs text-yellow-700">
                  <div>Técnico buscado: {selectedCita.tecnico}</div>
                  <div>Fecha buscada: {selectedCita.fechaISO}</div>
                  <div>Revisa los logs de la consola para más detalles</div>
                </div>
              </div>
            )}

            {/* Debug info */}
            {resultado.debug && (
              <details className="mt-4">
                <summary className="text-xs text-gray-500 cursor-pointer">🔧 Debug info</summary>
                <pre className="text-xs bg-gray-100 p-2 rounded mt-2 overflow-auto max-h-32">
                  {JSON.stringify(resultado.debug, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Mapa */}
      <div className="bg-white rounded-xl shadow-lg border p-4">
        <div className="h-[600px]">
          <MapComponent 
            center={mapCenter}
            markers={ordenesGeodificadas}
            clientMarker={clientLocation}
            loading={loadingMapa}
          />
        </div>
        
        {/* Información adicional del mapa */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">ℹ️ Información del mapa</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <div>• <strong>Marcador rojo:</strong> Ubicación del cliente (CP: {codigoPostal})</div>
            {selectedCita ? (
              <>
                <div>• <strong>Marcadores numerados:</strong> Órdenes de {selectedCita.tecnico} - {selectedCita.dia}</div>
                <div>• <strong>Total órdenes:</strong> {ordenesGeodificadas.length}</div>
                <div>• <strong>Ordenamiento:</strong> Del 1 (más lejano) al {ordenesGeodificadas.length} (más cercano a oficina)</div>
                <div>• <strong>Oficina:</strong> {OFICINA_COORDS.lat.toFixed(4)}, {OFICINA_COORDS.lng.toFixed(4)}</div>
              </>
            ) : (
              <div>• <strong>Selecciona una cita</strong> para ver las órdenes de trabajo</div>
            )}
            <div>• <strong>Fuente:</strong> Datos en tiempo real de StelOrder</div>
          </div>
        </div>

        {/* Estado de conexión */}
        <div className="mt-2 text-xs text-gray-500 text-center">
          🟢 Conectado vía Zapata Solutions
        </div>
      </div>
    </div>
  );
};

export default PlannerView;